import { defineStore } from 'pinia'
import { useFiltersStore } from './filters'

export const useTemplatesStore = defineStore('templates', {
    state: () => ({
        controller:     null,
        loading:        false,
        page:           1,
        limit:          24,
        limitAll:       10,
        templateGroups: [],
    }),
    getters: {
        /**
         * Collect all filters and return them as a single object
         *
         * @returns {Object}
         */
        filterParams() {
            const filtersStore = useFiltersStore()
            const type         = (this.$router.currentRoute.params && this.$router.currentRoute.params.type) ? this.$router.currentRoute.params.type : 'all'

            let params = {
                type:    type,
                keyword: filtersStore.keyword,
            }

            // Collect selected tags
            filtersStore.filterGroups.forEach(group => {
                let tags   = []

                group.filters.forEach(filter => {
                    if (filter.checked) {
                        tags.push(filter.slug)
                    }
                })

                // Add to a group if there are any tags
                if (tags.length) {
                    params['tags__' + group.id] = tags.join('|')
                }
            })

            // Add sorting
            params['sort'] = filtersStore.sortBy

            // Add pagination
            params['page']  = this.page

            return params
        },

        /**
         * Total number of templates found
         *
         * @returns {Number}
         */
        templateCount() {
            let count = 0

            this.templateGroups.forEach(group => {
                count += group.items.length
            })

            return count
        }
    },
    actions: {
        /**
         * Fetch initial templates
         */
        async fetchTemplates_deprecated() {
            if (! this.loading) {
                this.loading = true

                // Always reset pagination
                this.page = 1

                if (! this.filterParams.type || this.filterParams.type === 'all') {
                    // Set correct limit
                    this.filterParams.limit = this.limitAll

                    await this.$http.get('/templates/all', { params: this.filterParams }).then(async response => {
                        this.templateGroups = await response.data.data
                    }).catch(error => {
                        this.$toast.error('Error when fetching templates.')
                        console.error(error)
                    })
                } else {
                    // Set correct limit
                    this.filterParams.limit = this.limit

                    // And fetch
                    await this.$http.get('/templates/library/' + this.filterParams.type, { params: this.filterParams }).then(async response => {
                        let group = await response.data.data
                        this.templateGroups = [group]
                    }).catch(error => {
                        this.$toast.error('Error when fetching templates.')
                        console.error(error)
                    })
                }

                this.loading = false
            }
        },

        async fetchTemplates() {
            let useAll = !this.filterParams.type || this.filterParams.type === 'all',
                status = 'pending';

            return Promise.resolve()
                .then(() => {
                    if (this.loading)
                        this.controller.abort();
                })
                .then(() => {
                    this.controller = new AbortController();
                    this.loading = true;
                    this.page = 1;
                })
                .then(() => {
                    const url = useAll ? '/templates/all' : '/templates/library/' + this.filterParams.type,
                        config = {
                            params: this.filterParams,
                            signal: this.controller.signal,
                        };

                    return this.$http.get(url, config);
                })
                .then(async response => {
                    if (useAll)
                        this.templateGroups = await response.data.data;
                    else {
                        let group = await response.data.data;
                        this.templateGroups = [group];
                    }

                    status === 'success';
                })
                .catch(error => {
                    if (error && error.code === 'ERR_CANCELED') {
                        status = 'canceled';

                        return;
                    }

                    this.$toast.error('Error when fetching templates.');
                    console.error(error);

                    status = 'error';
                })
                .finally(() => {
                    if (status === 'canceled')
                        return;

                    this.page = 1;
                    this.loading = false;
                    this.controller = null;
                });
        },

        /**
         * Triggers when loading more templates
         *
         * @param {*} type
         * @param {*} pagination
         */
        async fetchMoreTemplates(type, pagination) {
            this.page = pagination.page

            // Set correct limit
            this.filterParams.limit = this.limit

            await this.$http.get('/templates/library/' + type, { params: this.filterParams }).then(async response => {
                let group = await response.data.data

                this.templateGroups.forEach((templateGroup) => {
                    if (group.slug === type) {
                        templateGroup.items.push(...group.items);
                    }
                })
            }).catch(error => {
                this.$toast.error('Error when fetching more templates.')
                console.error(error)
            })
        },

        async likeTemplate(uid) {
            await this.$http.post('/templates/like', { uid: uid }).then(async response => {
                await response.data.data
            }).catch(error => {
                this.$toast.error('Error when liking template.')
                console.error(error)
            })
        },

        async unlikeTemplate(uid) {
            await this.$http.post('/templates/unlike', { uid: uid }).then(async response => {
                await response.data.data
            }).catch(error => {
                this.$toast.error('Error when un-liking template.')
                console.error(error)
            })
        }
    }
})
